/* @flow */

import fscreen from 'fscreen';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { PipDisabler } from '../../helpers/player/pipDisabler';

import { logWarning, showDebug } from '../../helpers/debug/debug';
import HotKeys from '../../helpers/hotKeys/hotKeys';
import Localizer from '../../helpers/localization';
import { getBoundedValue } from '../../helpers/maths/maths';
import { Messenger, MessengerEvents } from '../../helpers/messenger';
import { ParentalControlLockState } from '../../helpers/parentalControl/constsAndTypes';
import { setupPipDisabler } from '../../helpers/player/pipDisabler';
import { getSkippingTypeAndValue } from '../../helpers/ui/player';
import { SkipDirection } from '../../helpers/ui/types';
import { updateVideoCarouselMuted, updateVideoCarouselPaused, updateVideoCarouselPlaying, updateVideoCarouselUnmuted } from '../../redux/ui/actions';
import ButtonBack from '../buttons/ButtonBack';
import './VideoCarousel.css';

const ONE_HUNDRED: number = 100;

// eslint-disable-next-line max-lines-per-function
const VideoCarousel = (): React.Node => {
  const dispatch = useDispatch();

  const isAnyModalOpen = useSelector<boolean>((state) => state.modal.openModals.length > 0);
  const gridSectionId = useSelector<string | null>((state) => state.ui.gridSectionId);
  const isDebugModeEnabled = useSelector<boolean>((state) => state.appConfiguration.isDebugModeEnabled);
  const isMuted = useSelector<boolean>((state) => state.ui.videoCarousel.isMuted);
  const isPlaying = useSelector<boolean>((state) => state.ui.videoCarousel.isPlaying);

  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const videoContainerRef = useRef<HTMLElement | null>(null);
  const isAutoplayEnabled = useRef<boolean>(true);

  const pipDisabler = useMemo<PipDisabler | null>(() => {
    if (!videoElementRef.current) {
      return null;
    }

    const pipDisabler = setupPipDisabler(videoElementRef.current);
    pipDisabler.setLocked(true);
    return pipDisabler;
  }, [videoElementRef.current]);

  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const [isHidden, setIsHidden] = useState<boolean>(gridSectionId !== null);
  const [isInitializingPlay, setIsInitializingPlay] = React.useState<boolean>(false);
  const [isMaskHidden, setIsMaskHidden] = useState<boolean>(false);
  const [trailerUrlList, setTrailerUrlList] = useState<Array<string> | null>(null);
  const [wasTrailerPlayingBeforePause, setWasTrailerPlayingBeforePause] = useState<boolean>(false);
  const [lockedIndices, setLockedIndices] = useState<Set<number>>(new Set<number>());

  const localShowDebug = useCallback(() => {
    if (!isDebugModeEnabled) {
      return;
    }

    showDebug('Background Trailers', {
      misc: { currentIndex, isHidden, isInitializingPlay, isMaskHidden, isMuted, isPlaying, trailerUrlList, wasTrailerPlayingBeforePause },
    });
  }, [currentIndex, isDebugModeEnabled, isHidden, isInitializingPlay, isMaskHidden, isMuted, isPlaying, trailerUrlList, wasTrailerPlayingBeforePause]);

  const isCurrentItemLocked = useCallback((): boolean => lockedIndices.has(currentIndex), [currentIndex, lockedIndices]);

  useEffect(() => {
    pipDisabler?.setLocked(isCurrentItemLocked());
  }, [isCurrentItemLocked, pipDisabler]);

  const onFullscreenChange = useCallback(() => {
    if (!videoContainerRef.current || !fscreen.fullscreenEnabled) {
      return;
    }

    if (fscreen.fullscreenElement !== null) {
      // Fullscreen activated
      videoContainerRef.current.classList.add('fullscreen');
      setIsMaskHidden(true);
    } else {
      // Fullscreen deactivated
      videoContainerRef.current.classList.remove('fullscreen');
      Messenger.emit(MessengerEvents.VIDEO_CAROUSEL_FULLSCREEN_EXITED);
      setIsMaskHidden(false);
    }
  }, []);

  const playVideo = useCallback(() => {
    if (!videoElementRef.current) {
      return;
    }

    if (!videoElementRef.current.paused) {
      return;
    }

    isAutoplayEnabled.current = true;
    setIsInitializingPlay(true);

    videoElementRef.current
      ?.play()
      .then(() => {
        setIsInitializingPlay(false);
      })
      .catch(() => {
        // Nothing to do (user probably navigated or changed video)
      });
  }, []);

  const pauseVideo = useCallback(() => {
    if (!videoElementRef.current) {
      return;
    }

    if (!isInitializingPlay) {
      videoElementRef.current.pause();
    }
  }, [isInitializingPlay]);

  const stopVideo = useCallback(() => {
    isAutoplayEnabled.current = false;
    setCurrentIndex(0);
    pauseVideo();
  }, [pauseVideo]);

  const show = useCallback(() => {
    setIsHidden(false);
  }, []);

  const hide = useCallback(() => {
    setIsHidden(true);
  }, []);

  const updateLockIndexState = useCallback((index: number, lockState: ParentalControlLockState) => {
    setLockedIndices((prevLockedIndices) => {
      const newLockedIndices = new Set(prevLockedIndices);

      if (lockState === ParentalControlLockState.Locked) {
        newLockedIndices.add(index);
      } else if (lockState === ParentalControlLockState.Unlocked) {
        newLockedIndices.delete(index);
      }

      return newLockedIndices;
    });
  }, []);

  const isInFullscreen = useCallback(() => fscreen.fullscreenElement !== null && fscreen.fullscreenEnabled, []);

  const enterFullscreen = useCallback(() => {
    if (!videoContainerRef.current || !fscreen.fullscreenEnabled || fscreen.fullscreenElement !== null) {
      return;
    }

    if (videoElementRef.current) {
      // Show video controls
      videoElementRef.current.controls = true;
    }

    fscreen.requestFullscreen(videoContainerRef.current).catch((error) => logWarning(`Fullscreen unavailable at the moment (${error.message})`));
  }, []);

  const exitFullscreen = useCallback(() => {
    if (!isInFullscreen()) {
      return;
    }

    if (videoElementRef.current) {
      // Hide video controls
      videoElementRef.current.controls = false;
    }

    fscreen.exitFullscreen();
    setIsMaskHidden(false);
  }, [isInFullscreen]);

  const toggleFullscreen = useCallback(() => {
    if (isInFullscreen()) {
      exitFullscreen();
    } else if (!isCurrentItemLocked()) {
      enterFullscreen();
    }
  }, [enterFullscreen, exitFullscreen, isCurrentItemLocked]);

  const handleOnDoubleClick = toggleFullscreen;

  const handleOnEnded = useCallback(() => {
    exitFullscreen();
    Messenger.emit(MessengerEvents.VIDEO_CAROUSEL_UPDATE_PROGRESS, currentIndex, 0);
    Messenger.emit(MessengerEvents.VIDEO_CAROUSEL_ENDED);
  }, [currentIndex, exitFullscreen]);

  const handleOnPause = useCallback(() => {
    dispatch(updateVideoCarouselPaused());
  }, []);

  const handleOnPlay = useCallback(() => {
    dispatch(updateVideoCarouselPlaying());
  }, []);

  const handleOnVolumeChange = useCallback(() => {
    if (!videoElementRef.current) {
      return;
    }

    if (videoElementRef.current.muted) {
      dispatch(updateVideoCarouselMuted());
    } else {
      dispatch(updateVideoCarouselUnmuted());
    }
  }, []);

  const handleOnTimeUpdate = useCallback(() => {
    if (!videoElementRef.current || isNaN(videoElementRef.current.duration)) {
      return;
    }

    const percent = Math.floor((videoElementRef.current.currentTime / videoElementRef.current.duration) * ONE_HUNDRED);
    Messenger.emit(MessengerEvents.VIDEO_CAROUSEL_UPDATE_PROGRESS, currentIndex, percent);
  }, [currentIndex]);

  const muteVideo = useCallback(() => {
    if (videoElementRef.current) {
      videoElementRef.current.muted = true;
    }
  }, []);

  const unmuteVideo = useCallback(() => {
    if (videoElementRef.current) {
      videoElementRef.current.muted = false;
    }
  }, []);

  const localSetCurrentIndex = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const handleTrailerHidden = useCallback(() => {
    if (!videoElementRef.current) {
      return;
    }

    setWasTrailerPlayingBeforePause(!videoElementRef.current.paused);
    pauseVideo();
  }, [pauseVideo]);

  const handleTrailerVisible = useCallback(() => {
    if (!videoElementRef.current) {
      return;
    }

    if (wasTrailerPlayingBeforePause) {
      setWasTrailerPlayingBeforePause(false);
      playVideo();
    }
  }, [playVideo, wasTrailerPlayingBeforePause]);

  const skipVideo = useCallback(
    (direction: SkipDirection, event: SyntheticKeyboardEvent<HTMLElement>) => {
      if (!videoElementRef.current || isNaN(videoElementRef.current.duration) || isCurrentItemLocked()) {
        return;
      }

      const { currentTime, duration } = videoElementRef.current;
      const { value } = getSkippingTypeAndValue(direction, event);

      // Flowjs thinks the previous line might have changed 'video'
      if (!videoElementRef.current) {
        return;
      }

      videoElementRef.current.currentTime = getBoundedValue(currentTime + value, 0, duration);
    },
    [isCurrentItemLocked],
  );

  const handleBackwardHotKey = useCallback(
    (event: SyntheticKeyboardEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      skipVideo(SkipDirection.Backward, event);
    },
    [skipVideo],
  );

  const handleForwardHotKey = useCallback(
    (event: SyntheticKeyboardEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      skipVideo(SkipDirection.Forward, event);
    },
    [skipVideo],
  );

  useEffect(() => {
    if (isPlaying) {
      playVideo();
    } else {
      pauseVideo();
    }
  }, [isPlaying]);

  useEffect(() => {
    if (isMuted) {
      muteVideo();
    } else {
      unmuteVideo();
    }
  }, [isMuted]);

  useEffect(() => {
    fscreen.addEventListener('fullscreenchange', onFullscreenChange, { passive: true });

    if (!isAnyModalOpen && !isHidden) {
      HotKeys.register('left', handleBackwardHotKey, { name: 'VideoCarousel.backward' });
      HotKeys.register('right', handleForwardHotKey, { name: 'VideoCarousel.forward' });
    }

    Messenger.on(MessengerEvents.CAROUSEL_SHOW_DEBUG, localShowDebug);
    Messenger.on(MessengerEvents.CAROUSEL_SET_CURRENT_INDEX, localSetCurrentIndex);
    Messenger.on(MessengerEvents.CAROUSEL_ITEM_LOCK_STATE_UPDATE, updateLockIndexState);
    Messenger.on(MessengerEvents.CAROUSEL_SHOW, show);
    Messenger.on(MessengerEvents.CAROUSEL_HIDE, hide);
    Messenger.on(MessengerEvents.VIDEO_CAROUSEL_SET, setTrailerUrlList);
    Messenger.on(MessengerEvents.VIDEO_CAROUSEL_STOP, stopVideo);
    Messenger.on(MessengerEvents.VIDEO_CAROUSEL_TOGGLE_FULLSCREEN, toggleFullscreen);
    Messenger.on(MessengerEvents.VIDEO_CAROUSEL_TRAILER_HIDDEN, handleTrailerHidden);
    Messenger.on(MessengerEvents.VIDEO_CAROUSEL_TRAILER_VISIBLE, handleTrailerVisible);

    return () => {
      fscreen.removeEventListener('fullscreenchange', onFullscreenChange, { passive: true });

      if (!isAnyModalOpen && !isHidden) {
        HotKeys.unregister('left', handleBackwardHotKey);
        HotKeys.unregister('right', handleForwardHotKey);
      }

      Messenger.off(MessengerEvents.CAROUSEL_SHOW_DEBUG, localShowDebug);
      Messenger.off(MessengerEvents.CAROUSEL_SET_CURRENT_INDEX, localSetCurrentIndex);
      Messenger.off(MessengerEvents.CAROUSEL_ITEM_LOCK_STATE_UPDATE, updateLockIndexState);
      Messenger.off(MessengerEvents.CAROUSEL_SHOW, show);
      Messenger.off(MessengerEvents.CAROUSEL_HIDE, hide);
      Messenger.off(MessengerEvents.VIDEO_CAROUSEL_SET, setTrailerUrlList);
      Messenger.off(MessengerEvents.VIDEO_CAROUSEL_STOP, stopVideo);
      Messenger.off(MessengerEvents.VIDEO_CAROUSEL_TOGGLE_FULLSCREEN, toggleFullscreen);
      Messenger.off(MessengerEvents.VIDEO_CAROUSEL_TRAILER_HIDDEN, handleTrailerHidden);
      Messenger.off(MessengerEvents.VIDEO_CAROUSEL_TRAILER_VISIBLE, handleTrailerVisible);
    };
  }, [
    enterFullscreen,
    exitFullscreen,
    handleBackwardHotKey,
    handleForwardHotKey,
    handleTrailerHidden,
    handleTrailerVisible,
    hide,
    isAnyModalOpen,
    localSetCurrentIndex,
    localShowDebug,
    onFullscreenChange,
    setTrailerUrlList,
    show,
    stopVideo,
    toggleFullscreen,
    updateLockIndexState,
  ]);

  if (isHidden || !trailerUrlList || currentIndex === -1) {
    return false;
  }

  const url = trailerUrlList[currentIndex];
  const maskElement = isMaskHidden ? null : <div className='mask' />;

  return (
    <div className='videoCarousel' ref={videoContainerRef}>
      <div className='videoWrapper'>
        <video
          autoPlay={Boolean(isAutoplayEnabled.current) && !isCurrentItemLocked()}
          controlsList='nofullscreen nodownload noremoteplayback'
          muted={isMuted}
          onDoubleClick={handleOnDoubleClick}
          onEnded={handleOnEnded}
          onPause={handleOnPause}
          onPlay={handleOnPlay}
          onTimeUpdate={handleOnTimeUpdate}
          onVolumeChange={handleOnVolumeChange}
          ref={videoElementRef}
          src={url}
        />
        {maskElement}
      </div>
      <ButtonBack aria-label={Localizer.localize('accessibility.player.exit')} className='backBar' hasText={false} onClick={exitFullscreen} />
    </div>
  );
};

export default VideoCarousel;
